/**
 * Recado que o produtor manda para quem comprou o ingresso, junto com um link
 * opcional. Sai no e-mail de pedido aprovado e nunca na página do evento.
 *
 * O texto é HTML do editor (o mesmo das campanhas). Ingressos salvos antes do
 * editor têm texto puro, que continua valendo: a API e este arquivo aceitam os dois.
 *
 * Os limites espelham a validação da API (products.buyer_message*): o de 1.000
 * vale para o texto que o comprador lê, o do HTML é só o teto de armazenamento.
 */
export const BUYER_MESSAGE_MAX_LENGTH = 1000;
export const BUYER_MESSAGE_HTML_MAX_LENGTH = 15000;
export const BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH = 80;

// Mesmas tags que a API reconhece (App\Support\EmailRichText): sem nenhuma, é texto puro.
const EDITOR_TAG = /<\/?(?:p|br|strong|b|em|i|u|s|strike|ol|ul|li|a|span|div)\b[^>]*>/i;

const isHtml = (value) => EDITOR_TAG.test(value || '');

function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/** O texto que o comprador lê, sem formatação. */
export function buyerMessageText(message) {
    if (!message) return '';

    if (!isHtml(message)) return message.trim();

    // DOMParser monta um documento inerte: nada é executado nem baixado.
    const document = new DOMParser().parseFromString(message, 'text/html');

    return (document.body.textContent || '').trim();
}

/** Imagem ainda subindo: o editor guarda o base64 até a URL definitiva chegar. */
export function hasPendingBuyerMessageImage(message) {
    return /<img\b[^>]*\bsrc=["']data:/i.test(message || '');
}

/** O editor deixa `<p><br></p>` quando se apaga tudo: continua sendo "sem recado". */
export function isBuyerMessageEmpty(message) {
    return buyerMessageText(message) === '' && !/<img\b/i.test(message || '');
}

/** Texto puro antigo vira parágrafos, para abrir no editor com as quebras de linha. */
export function buyerMessageToEditorHtml(message) {
    if (!message || isBuyerMessageEmpty(message)) return '';

    if (isHtml(message)) return message;

    return message
        .split(/\r?\n/)
        .map(line => `<p>${line.trim() ? escapeHtml(line) : '<br>'}</p>`)
        .join('');
}

/**
 * A API só aceita http(s). Produtor costuma colar "chat.whatsapp.com/…" sem o
 * esquema, então completamos com https em vez de recusar.
 */
export function normalizeBuyerMessageLink(link) {
    const trimmed = (link || '').trim();

    if (!trimmed) return null;

    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;

    return `https://${trimmed}`;
}

export function isBuyerMessageLinkValid(link) {
    const normalized = normalizeBuyerMessageLink(link);

    if (!normalized) return true;

    try {
        const url = new URL(normalized);
        return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
    } catch {
        return false;
    }
}

export function isBuyerMessageValid(ticket) {
    const message = ticket.buyer_message || '';
    const label = ticket.buyer_message_link_label || '';

    return (
        buyerMessageText(message).length <= BUYER_MESSAGE_MAX_LENGTH &&
        message.length <= BUYER_MESSAGE_HTML_MAX_LENGTH &&
        !hasPendingBuyerMessageImage(message) &&
        label.length <= BUYER_MESSAGE_LINK_LABEL_MAX_LENGTH &&
        isBuyerMessageLinkValid(ticket.buyer_message_link_url)
    );
}

/** Campos do recado prontos para o payload: vazio vira null, o link é normalizado. */
export function buyerMessagePayload(ticket) {
    const message = (ticket.buyer_message || '').trim();
    const link = normalizeBuyerMessageLink(ticket.buyer_message_link_url);
    const label = (ticket.buyer_message_link_label || '').trim();

    return {
        buyer_message: isBuyerMessageEmpty(message) ? null : message,
        buyer_message_link_url: link,
        buyer_message_link_label: link && label ? label : null,
    };
}
